import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useKnowledgeBase } from './hooks';
import { PageLoader, Button, FullWidthContainer, Input, Select, Modal } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { useToast } from '../../contexts/useToast';
import {
  KnowledgeBaseMetrics,
  DocumentsTable,
  KnowledgeBasesGrid,
  UploadDocumentModal,
  EditDocumentModal,
  EditKnowledgeBaseModal,
} from './components';
import { UploadDocumentData, CreateKnowledgeBaseData, Document, KnowledgeBase } from './types';

const KnowledgeBasePage: React.FC = () => {
  const { t } = useTranslation();
  const {
    stats,
    documents,
    knowledgeBases,
    initialLoading,
    filterLoading,
    error,
    filters,
    setFilters,
    uploadDocument,
    createKnowledgeBase,
    deleteDocument,
    deleteKnowledgeBase,
    updateDocument,
    updateKnowledgeBase,
    fetchDocumentsWithFilters,
  } = useKnowledgeBase();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<'documents' | 'knowledge-bases'>('documents');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditKnowledgeBaseModalOpen, setIsEditKnowledgeBaseModalOpen] = useState(false);
  const [isDeleteKnowledgeBaseModalOpen, setIsDeleteKnowledgeBaseModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const showLoader = useMinimumLoader(initialLoading, { minimumDuration: 500 });
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleUploadDocument = async (data: UploadDocumentData) => {
    try {
      await uploadDocument(data);
      setIsUploadModalOpen(false);
      showToast({
        type: 'success',
        message: 'Document uploaded successfully!',
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to upload document',
      });
    }
  };

  const handleCreateKnowledgeBase = async (data: CreateKnowledgeBaseData) => {
    try {
      await createKnowledgeBase(data);
      showToast({
        type: 'success',
        message: 'Knowledge base created successfully!',
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to create knowledge base',
      });
    }
  };

  const handleDeleteDocument = (id: string) => {
    const doc = documents.find(doc => doc.id === id);
    if (doc) {
      setSelectedDocument(doc);
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDeleteDocument = async () => {
    if (selectedDocument) {
      try {
        await deleteDocument(selectedDocument.id);
        showToast({
          type: 'success',
          message: 'Document deleted successfully!',
        });
        setIsDeleteModalOpen(false);
        setSelectedDocument(null);
      } catch (err) {
        showToast({
          type: 'error',
          message: 'Failed to delete document',
        });
      }
    }
  };

  const handleDeleteKnowledgeBase = (id: string) => {
    const kb = knowledgeBases.find(kb => kb.id === id);
    if (kb) {
      setSelectedKnowledgeBase(kb);
      setIsDeleteKnowledgeBaseModalOpen(true);
    }
  };

  const confirmDeleteKnowledgeBase = async () => {
    if (selectedKnowledgeBase) {
      try {
        await deleteKnowledgeBase(selectedKnowledgeBase.id);
        showToast({
          type: 'success',
          message: 'Knowledge base deleted successfully!',
        });
        setIsDeleteKnowledgeBaseModalOpen(false);
        setSelectedKnowledgeBase(null);
      } catch (err) {
        showToast({
          type: 'error',
          message: 'Failed to delete knowledge base',
        });
      }
    }
  };

  const handleDownloadDocument = async (id: string) => {
    try {
      const doc = documents.find(doc => doc.id === id);
      if (doc && doc.file) {
        const link = document.createElement('a');
        link.href = doc.file;
        link.download = doc.title;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast({
          type: 'success',
          message: 'Document download started!',
        });
      } else {
        showToast({
          type: 'error',
          message: 'Document not found or file URL not available',
        });
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to download document',
      });
    }
  };

  const handleEditDocument = (id: string) => {
    const doc = documents.find(doc => doc.id === id);
    if (doc) {
      setSelectedDocument(doc);
      setIsEditModalOpen(true);
    }
  };

  const handleEditDocumentSubmit = async (data: { title: string; tags: string }) => {
    if (selectedDocument) {
      try {
        await updateDocument(selectedDocument.id, data);
        showToast({
          type: 'success',
          message: 'Document updated successfully!',
        });
        setIsEditModalOpen(false);
        setSelectedDocument(null);
      } catch (err) {
        showToast({
          type: 'error',
          message: 'Failed to update document',
        });
      }
    }
  };

  const handleDownloadKnowledgeBase = async (id: string) => {
    try {
      const kb = knowledgeBases.find(kb => kb.id === id);
      if (kb) {
        const downloadUrl = `/api/knowledge-base/${id}/export/`;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${kb.name}_knowledge_base.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast({
          type: 'success',
          message: 'Knowledge base download started!',
        });
      } else {
        showToast({
          type: 'error',
          message: 'Knowledge base not found',
        });
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to download knowledge base',
      });
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    fetchDocumentsWithFilters();
  };

  useEffect(() => {
    if (activeTab === 'documents') {
      pollingIntervalRef.current = setInterval(() => {
        fetchDocumentsWithFilters();
      }, 10000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [activeTab, fetchDocumentsWithFilters]);

  const handleEditKnowledgeBase = (id: string) => {
    const kb = knowledgeBases.find(kb => kb.id === id);
    if (kb) {
      setSelectedKnowledgeBase(kb);
      setIsEditKnowledgeBaseModalOpen(true);
    }
  };

  const handleEditKnowledgeBaseSubmit = async (data: {
    name: string;
    description: string;
    tags: string;
  }) => {
    if (selectedKnowledgeBase) {
      try {
        await updateKnowledgeBase(selectedKnowledgeBase.id, data);
        showToast({
          type: 'success',
          message: 'Knowledge base updated successfully!',
        });
        setIsEditKnowledgeBaseModalOpen(false);
        setSelectedKnowledgeBase(null);
      } catch (err) {
        showToast({
          type: 'error',
          message: 'Failed to update knowledge base',
        });
      }
    }
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="space-y-6">
          <div className="text-center py-8">
            <p className="text-red-500 text-lg font-semibold">Error loading knowledge base</p>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
          </div>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('knowledgeBase.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('knowledgeBase.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="gradient-multi"
              onClick={() => setIsUploadModalOpen(true)}
              className="flex items-center gap-2"
            >
              <span className="material-icons">upload_file</span>
              {t('knowledgeBase.uploadDocument')}
            </Button>
          </div>
        </div>

        {/* Metrics Cards */}
        <KnowledgeBaseMetrics stats={stats} />

        {/* Tab Navigation */}
        <div className="max-w-4xl">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('documents')}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'documents'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <span className="material-icons text-sm mr-2">description</span>
              {t('knowledgeBase.documents')}
            </button>
            <button
              onClick={() => setActiveTab('knowledge-bases')}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'knowledge-bases'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <span className="material-icons text-sm mr-2">folder</span>
              {t('knowledgeBase.knowledgeBases')}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'documents' && (
          <div className="max-w-4xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('knowledgeBase.documentManagement')}
              </h2>
              <div className="flex gap-4">
                <Input
                  type="text"
                  placeholder={t('knowledgeBase.searchDocuments')}
                  value={filters.search}
                  onChange={e => handleFilterChange({ ...filters, search: e.target.value })}
                  className="w-80 h-10"
                />
                <Select
                  value={filters.status}
                  onChange={value =>
                    handleFilterChange({
                      ...filters,
                      status: Array.isArray(value) ? value[0] : value,
                    })
                  }
                  options={[
                    { value: '', label: t('knowledgeBase.allStatus') },
                    { value: 'uploaded', label: t('knowledgeBase.uploaded') },
                    { value: 'processing', label: t('knowledgeBase.processing') },
                    { value: 'processed', label: t('knowledgeBase.processed') },
                    { value: 'failed', label: t('knowledgeBase.failed') },
                  ]}
                  className="w-40 h-10"
                />
                <Select
                  value={filters.knowledgeBase}
                  onChange={value =>
                    handleFilterChange({
                      ...filters,
                      knowledgeBase: Array.isArray(value) ? value[0] : value,
                    })
                  }
                  options={[
                    { value: '', label: t('knowledgeBase.allKb') },
                    ...knowledgeBases.map(kb => ({
                      value: kb.id,
                      label: kb.name,
                    })),
                  ]}
                  className="w-48 h-10"
                />
              </div>
            </div>
            <DocumentsTable
              documents={documents}
              onDownload={handleDownloadDocument}
              onEdit={handleEditDocument}
              onDelete={handleDeleteDocument}
              onUploadDocument={() => setIsUploadModalOpen(true)}
              loading={filterLoading}
            />
          </div>
        )}

        {activeTab === 'knowledge-bases' && (
          <div className="max-w-4xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('knowledgeBase.knowledgeBaseManagement')}
              </h2>
              <Button
                variant="gradient-multi"
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2"
              >
                <span className="material-icons">add</span>
                {t('knowledgeBase.createKnowledgeBase')}
              </Button>
            </div>
            <KnowledgeBasesGrid
              knowledgeBases={knowledgeBases}
              onCreate={handleCreateKnowledgeBase}
              onDownload={handleDownloadKnowledgeBase}
              onEdit={handleEditKnowledgeBase}
              onDelete={handleDeleteKnowledgeBase}
              showCreateForm={showCreateForm}
              setShowCreateForm={setShowCreateForm}
            />
          </div>
        )}

        {/* Upload Document Modal */}
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onSubmit={handleUploadDocument}
          knowledgeBases={knowledgeBases}
        />

        {/* Delete Document Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedDocument(null);
          }}
          title=""
          size="sm"
          showCloseButton={false}
        >
          <div className="text-center space-y-6">
            {/* Warning Icon */}
            <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <span className="material-icons text-3xl text-red-600 dark:text-red-400">
                delete_forever
              </span>
            </div>

            {/* Content */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('knowledgeBase.deleteDocument')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('knowledgeBase.areYouSureDeleteDocument')}{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  &ldquo;{selectedDocument?.title}&rdquo;
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('knowledgeBase.thisActionCannotBeUndoneDocument')}
              </p>
            </div>

            {/* Document Info Card */}
            {selectedDocument && (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                    <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                      description
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedDocument.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedDocument.fileSizeVerbose} • {selectedDocument.knowledgeBase.name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedDocument(null);
                }}
                className="flex-1 px-6 py-3 font-medium"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={confirmDeleteDocument}
                className="flex-1 px-6 py-3 font-medium"
              >
                <span className="material-icons text-sm mr-2">delete</span>
                {t('knowledgeBase.deleteDocument')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit Document Modal */}
        <EditDocumentModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedDocument(null);
          }}
          onSubmit={handleEditDocumentSubmit}
          document={selectedDocument}
          knowledgeBases={knowledgeBases}
        />

        {/* Edit Knowledge Base Modal */}
        <EditKnowledgeBaseModal
          isOpen={isEditKnowledgeBaseModalOpen}
          onClose={() => {
            setIsEditKnowledgeBaseModalOpen(false);
            setSelectedKnowledgeBase(null);
          }}
          onSubmit={handleEditKnowledgeBaseSubmit}
          knowledgeBase={selectedKnowledgeBase}
        />

        {/* Delete Knowledge Base Modal */}
        <Modal
          isOpen={isDeleteKnowledgeBaseModalOpen}
          onClose={() => {
            setIsDeleteKnowledgeBaseModalOpen(false);
            setSelectedKnowledgeBase(null);
          }}
          title=""
          size="sm"
          showCloseButton={false}
        >
          <div className="text-center space-y-6">
            {/* Warning Icon */}
            <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <span className="material-icons text-3xl text-red-600 dark:text-red-400">
                delete_forever
              </span>
            </div>

            {/* Content */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('knowledgeBase.deleteKnowledgeBase')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('knowledgeBase.areYouSureDeleteKnowledgeBase')}{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  &ldquo;{selectedKnowledgeBase?.name}&rdquo;
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('knowledgeBase.thisActionCannotBeUndoneKnowledgeBase')}
              </p>
            </div>

            {/* Knowledge Base Info Card */}
            {selectedKnowledgeBase && (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                    <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                      folder
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedKnowledgeBase.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedKnowledgeBase.documentCount} documents •{' '}
                      {selectedKnowledgeBase.status}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsDeleteKnowledgeBaseModalOpen(false);
                  setSelectedKnowledgeBase(null);
                }}
                className="flex-1 px-6 py-3 font-medium"
              >
                {t('knowledgeBase.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={confirmDeleteKnowledgeBase}
                className="flex-1 px-6 py-3 font-medium"
              >
                <span className="material-icons text-sm mr-2">delete</span>
                {t('knowledgeBase.deleteKnowledgeBase')}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </FullWidthContainer>
  );
};

export { KnowledgeBasePage as KnowledgeBase };
